import React, {
	useEffect,
	useState,
	useCallback,
	useRef,
	useMemo,
} from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { format } from 'date-fns';
import { Campaigns, SpinnerDialog } from '@silverrailtech/srtlibrary';
import PropTypes from 'prop-types';
import Header from '../components/Header/Header';
import { GlobalCampaignStyle, CampaignsWrapper } from './DashboardStyled';
import {
	getCampaigns,
	getPromotionCampaignReports,
	getVoucherCampaignReports,
	getPromotionCampaignDetails,
	getVoucherCampaignDetails,
	createPromotionCampaign,
	createVoucherCampaign,
	updatePromotionCampaign,
	updateVoucherCampaign,
	deletePromotionCampaign,
	deleteVoucherCampaign,
	asyncApiCall,
	issueMultipleVoucher,
	issueMultiplePromotion,
} from '../api';
import ExtendedSnackbar from '../components/ExtendedSnackbar/ExtendedSnackbar';
import { stationSearchApi } from '../Shopping/apiShopping';
import useIssuanceOfferFlow from './useIssuanceOfferFlow';
import useRequestor from '../hooks/useRequestor';
import {
	campaignFormInitialState,
	isVoucherCampaign,
	getCampaignType,
	getFormattedChannels,
	getRedemptionChannels,
	getInitialChannels,
	formatStationsResponses,
	getStationsNamesFallback,
	getFareCodes,
	getRoutes,
	handleCsvDownload,
	enrichCsvData,
	getEnhancedSettlementDetails,
	getSettlementDetailsData,
	getFareClasses,
	getFormattedFareTypes,
	getTravelerRestrictions,
	getFulfilmentsRules,
	getFormattedFulfilments,
	UK_RAILCARDS_SUPPLIER,
	generateRailcardsData,
	parseRailcardsData,
	getTravelDateTime,
	getFormattedDateOfTravel,
	getCalculationType,
	CALCULATION_TYPES,
	getValidPassengers,
	getInitialRailcards,
	getRedemptionRule,
	parseRedemptionRule,
	DEFAULT_REDEMPTIONS_TARGETED,
	INTEGER_MAX_VALUE,
	isUnlimitedValue,
	isTargetedPromotion,
	availableFulfilments,
	CALCULATION_RULE_ROUNDING_METHOD,
	normalizeRedemptionHistory,
	normalizePerformance,
	normalizeReportDateRange,
} from './utils';
import { getStationDataApi } from '../components/apiComponents';
import { setActiveRealm } from '../actions/authActions';

const CampaignsDashboard = ({
	loginId,
	settings,
	intl,
	railCards,
	activeRealm,
	setActiveRealm: setActiveRealmAction,
}) => {
	const [filteredCampaigns, setFilteredCampaigns] = useState([]);
	const [reports, setReports] = useState({});
	const [formData, setFormData] = useState(campaignFormInitialState);
	const [editedFormData, setEditedFormData] = useState(
		campaignFormInitialState,
	);
	const [stations, setStations] = useState([]);
	const [isStationLoading, setIsStationLoading] = useState(false);
	const [modalConfig, setModalConfig] = useState({
		isOpen: false,
		type: null,
	});
	const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isDuplicateMode, setIsDuplicateMode] = useState(false);
	const hasSettings = loginId && settings;
	const [alertText, setAlertText] = useState('');
	const [isCampaignDataFetching, setIsCampaignDataFetching] = useState(false);

	const [isMultipleOfferFetching, setIsMultipleOfferFetching] = useState(false);
	const [multipleOfferError, setMultipleOfferError] = useState('');
	const [multipleOfferSuccess, setMultipleOfferSuccess] = useState(false);

	const { channels, requestorId, accountRealms } = useRequestor();

	const filterAccountRealmsByRequestorId = useCallback(
		(realms) =>
			(realms || []).filter(
				(realm) => realm.SecurityAuthenticationRequestorId != null,
			),
		[],
	);

	const filteredAccountRealms = useMemo(
		() => filterAccountRealmsByRequestorId(accountRealms),
		[accountRealms, filterAccountRealmsByRequestorId],
	);

	const railCardsUK = useMemo(
		() =>
			railCards.filter(
				(card) => card.RailCardSupplier === UK_RAILCARDS_SUPPLIER,
			),
		[railCards],
	);

	useEffect(() => {
		if (!activeRealm && filteredAccountRealms.length > 0) {
			setActiveRealmAction(filteredAccountRealms[0]);
		}
	}, [activeRealm, filteredAccountRealms, setActiveRealmAction]);

	const handleCampaigns = useCallback(
		(search = '') => {
			setIsCampaignDataFetching(true);
			getCampaigns(
				{ name: search },
				requestorId,
				() => {
					setAlertText('Failed to load campaigns');
				},
				(data) => {
					try {
						setFilteredCampaigns(
							data.map((campaign) => ({
								campaignType: getCampaignType(
									campaign.source.campaign_type,
									campaign.source.pin_required,
								),
								campaignCalculationType:
									campaign.source.campaign_calculation_type,
								campaignName: campaign.source.campaign_name,
								promotionCode: campaign.source.campaign_code,
								accountName: campaign.account.account_code,
								pinRequired: campaign.source.pin_required,
								description: campaign.source.campaign_description,
								discount:
									campaign.source.campaign_calculation_type !==
									'PERCENTAGE_DISCOUNT',
								discountValue: campaign.source.campaign_value,
								startDate: campaign.source.campaign_start_date,
								endDate: campaign.source.campaign_end_date,
								noEndDate: !campaign.source.campaign_end_date,
								active: campaign.source.campaign_status === 'ACTIVE',
								promotionCount: campaign.source.promotion_count,
								voucherCount: campaign.source.voucher_count,
								operators: campaign.campaign_operators ?? {
									isSpecified: false,
									selected: { lumo: true, gwr: true, swr: false, hull: true },
								},
								channels: {
									isSpecified: false,
									selected: [],
								},
								fulfilments: {
									isSpecified: false,
									selected: [],
								},
								dateOfTravel: campaign.source.campaign_time_of_travel ?? {
									isSpecified: false,
									selected: [],
								},
								fareTypes: campaign.campaign_fare_types ?? {
									isSpecified: false,
									selected: [],
								},
								fareCodes: {
									isSpecified: false,
									selected: [],
								},
								validRoutes: campaign.source.campaign_valid_routes ?? {
									isSpecified: false,
									selected: [],
								},
								validPassengers: campaign.source.campaign_valid_passengers ?? {
									isSpecified: false,
									selected: [],
								},
								railcards: getInitialRailcards(railCardsUK),
								validityPeriod: {
									isSlidingRange: false,
									startDate: '',
									startTime: '',
									noStartDate: false,
									endDate: '',
									endTime: '',
									noEndDate: false,
									duration: '0',
									durationUnit: 'days',
								},
								settlementData: {
									settlementType: '',
									discountCode: '',
									promotionCode: '',
									sundryCode: '',
									refundSundryCode: '',
								},
							})),
						);
					} catch (processingError) {
						setFilteredCampaigns([]);
					} finally {
						setIsCampaignDataFetching(false);
					}
				},
			);
		},
		[requestorId],
	);

	const onRealmChange = (realm) => {
		setActiveRealmAction(realm);
	};

	useEffect(() => {
		if (requestorId) {
			handleCampaigns();
		}
	}, [requestorId, handleCampaigns]);

	const handleSearch = (search) => {
		handleCampaigns(search);
	};

	const createNewCampaign = () => {
		setIsEditMode(false);
		setIsDuplicateMode(false);
		setIsCampaignModalOpen(true);

		const channelsAvailable = getInitialChannels(channels);

		const campaignChannels = {
			isSpecified: false,
			selected: channelsAvailable,
		};

		const campaignRailcards = getInitialRailcards(railCardsUK);

		setEditedFormData(() => ({
			...campaignFormInitialState,
			channels: campaignChannels,
			railcards: campaignRailcards,
		}));

		setFormData(() => ({
			...campaignFormInitialState,
			channels: campaignChannels,
			railcards: campaignRailcards,
		}));
	};

	const fetchStationNames = async (codes) => {
		try {
			const results = await Promise.all(
				codes.map((code) => asyncApiCall(getStationDataApi, code)),
			);

			return formatStationsResponses(results, codes);
		} catch (error) {
			return getStationsNamesFallback(codes);
		}
	};

	const getFormattedCampaign = async (
		campaign,
		promotionCount,
		voucherCount,
	) => {
		const amountValue = campaign.calculation_rule?.value_amount;
		const percentageValue =
			campaign.calculation_rule?.calculation_type !==
			CALCULATION_TYPES.PERCENTAGE_DISCOUNT;

		const dailySchedules = [];

		const formattedFareCodes = getFareCodes(
			campaign.travel_restrictions.fare_codes,
		);

		const enhancedSettlementDetails = getEnhancedSettlementDetails(campaign);

		const { isChannelSpecified, channelsAvailable } = getFormattedChannels(
			channels,
			campaign.redemption_channels,
		);

		const stationNames = await fetchStationNames(
			(campaign.travel_restrictions?.routes || []).flatMap((route) =>
				[route.origin_station_code, route.destination_station_code].filter(
					(code) => code != null,
				),
			),
		);

		const routes = getRoutes(
			campaign.travel_restrictions?.routes,
			false,
			stationNames,
		);

		const validPassengers = getValidPassengers(campaign?.traveler_restrictions);

		campaign.travel_restrictions?.daily_schedules.forEach((schedule) => {
			dailySchedules.push({
				startTime: schedule.outbound_departure_start_time,
				endTime: schedule.outbound_departure_end_time,
				outbound_arrival_start_time: schedule.outbound_arrival_start_time,
				outbound_arrival_end_time: schedule.outbound_arrival_end_time,
			});
		});

		const { railcardIsSpecified, railcardSelected } = generateRailcardsData(
			campaign.travel_restrictions?.travel_restriction_fare_qualifiers,
			campaign.travel_restrictions?.travel_restriction_allowed_fare_qualifier,
		);

		return {
			campaignType: getCampaignType(
				campaign.campaign_type,
				campaign.pin_required,
			),
			campaignName: campaign.name,
			campaignCalculationType: campaign.calculation_rule.calculation_type,
			promotionCode: campaign.campaign_code,
			accountName: campaign.account_code,
			pinRequired: campaign.pin_required,
			description: campaign.description,
			discount: percentageValue,
			discountValue: amountValue,
			adultDiscountValue: amountValue,
			childDiscountValue: campaign.calculation_rule?.value_amount_child,
			quantityAllowedValue: isUnlimitedValue(
				campaign.issuance_rule?.max_number_of_issuances,
			)
				? ''
				: campaign.issuance_rule?.max_number_of_issuances,
			quantityAllowed: !isUnlimitedValue(
				campaign.issuance_rule?.max_number_of_issuances,
			),
			minimumSpentValue: campaign.calculation_rule?.min_price_threshold_amount,
			minimumSpent: campaign.calculation_rule?.min_price_threshold_amount > 0,
			startDate: campaign.issuance_rule?.issuance_start_date,
			startTime: format(
				new Date(campaign.issuance_rule?.issuance_start_date),
				'HH:mm',
			),
			endTime: format(
				new Date(campaign.issuance_rule?.issuance_end_date),
				'HH:mm',
			),
			endDate: campaign.issuance_rule?.issuance_end_date,
			noEndDate: !campaign.issuance_rule?.issuance_end_date,
			noStartDate: !campaign.issuance_rule?.issuance_start_date,
			active: campaign.status === 'ACTIVE',
			promotionCount,
			voucherCount,
			operators: campaign.campaign_operators ?? {
				isSpecified: false,
				selected: [],
			},
			channels: {
				isSpecified: isChannelSpecified,
				selected: channelsAvailable,
			},
			fulfilments: getFormattedFulfilments(
				campaign.travel_restrictions?.ticket_delivery_rules,
				availableFulfilments,
			),
			validRoutes: {
				isSpecified: routes.length > 0,
				selected: routes,
			},
			dateOfTravel: getFormattedDateOfTravel(
				campaign.travel_restrictions?.travel_date_time,
			),
			fareTypes: getFormattedFareTypes(
				campaign.travel_restrictions?.fare_classes,
			),
			fareCodes: {
				isSpecified: formattedFareCodes.length > 0,
				selected: formattedFareCodes,
			},
			validPassengers: {
				isSpecified: validPassengers.length > 0,
				selected: validPassengers,
			},
			railcards: {
				isSpecified: railcardIsSpecified,
				selected: railcardSelected,
				options: railCardsUK,
			},
			validityPeriod: parseRedemptionRule(campaign.redemption_rule),
			settlementData: enhancedSettlementDetails,
			redemptionsAllowed: !isUnlimitedValue(
				campaign.redemption_rule?.max_number_of_redemptions,
			),
			maxNumberOfRedemptions: isUnlimitedValue(
				campaign.redemption_rule?.max_number_of_redemptions,
			)
				? ''
				: campaign.redemption_rule?.max_number_of_redemptions,
		};
	};

	const getCampaignDetails = (campaign, isDuplicate = false) => {
		if (isVoucherCampaign(campaign.campaignType)) {
			getVoucherCampaignDetails(
				{},
				requestorId,
				campaign.promotionCode,
				(error) => {
					setAlertText(
						error.message ?? 'Failed to load voucher campaign details',
					);
					setIsCampaignDataFetching(false);
				},
				async (data) => {
					let formattedCampaign = await getFormattedCampaign(
						data,
						campaign.promotionCount,
						campaign.voucherCount,
					);
					if (isDuplicate) {
						formattedCampaign = {
							...formattedCampaign,
							campaignName: '',
							promotionCode: '',
						};
					}
					setReports({});
					setFormData(formattedCampaign);
					setEditedFormData(formattedCampaign);
					setIsCampaignModalOpen(true);
					setIsCampaignDataFetching(false);
				},
			);
		} else {
			getPromotionCampaignDetails(
				{},
				requestorId,
				campaign.promotionCode,
				(error) => {
					setAlertText(
						error.message ?? 'Failed to load promotion campaign details',
					);
					setIsCampaignDataFetching(false);
				},
				async (data) => {
					let formattedCampaign = await getFormattedCampaign(
						data,
						campaign.promotionCount,
						campaign.voucherCount,
					);
					if (isDuplicate) {
						formattedCampaign = {
							...formattedCampaign,
							campaignName: '',
							promotionCode: '',
						};
					}
					setReports({});
					setFormData(formattedCampaign);
					setEditedFormData(formattedCampaign);
					setIsCampaignModalOpen(true);
					setIsCampaignDataFetching(false);
				},
			);
		}
	};

	const handleViewEdit = (campaign) => {
		setIsEditMode(true);
		setIsCampaignDataFetching(true);
		getCampaignDetails(campaign);
	};

	const onViewReports = useCallback(
		(campaign) => {
			setReports({});

			if (isVoucherCampaign(campaign.campaignType)) {
				getVoucherCampaignReports(
					{},
					requestorId,
					campaign.promotionCode,
					(error) => {
						setAlertText(
							error.message ?? 'Failed to load voucher campaign reports',
						);
					},
					(data) => {
						const voucher = data.vouchers?.[0];

						const redemptionHistory = normalizeRedemptionHistory(
							data.vouchers,
							intl,
						);

						const performance = normalizePerformance(
							data.vouchers,
							redemptionHistory,
						);

						setReports({
							redemptionHistory,
							campaign: data,
							summary: {
								...normalizeReportDateRange(voucher),
								campaignType: data.campaign_type,
								name: data.name,
								code: data.campaign_code,
								value: voucher?.calculation_rule?.value_amount ?? 0,
								status: data?.status,
								pinRequired: data?.pin_required,
								generation: data?.pin_creation_method,
								description: data?.description,
								channels:
									voucher?.redemption_channels
										?.map((channel) => channel.redemption_channel_code)
										.join(', ') ?? '',
							},
							performance,
						});
					},
				);
			} else {
				getPromotionCampaignReports(
					{},
					requestorId,
					campaign.promotionCode,
					(error) => {
						setAlertText(
							error.message ?? 'Failed to load promotion campaign reports',
						);
					},
					(data) => {
						const promotion = data.promotions?.[0];

						const redemptionHistory = normalizeRedemptionHistory(
							data.promotions,
							intl,
						);

						const performance = normalizePerformance(
							data.vouchers,
							redemptionHistory,
						);

						setReports({
							redemptionHistory,
							campaign: data,
							summary: {
								...normalizeReportDateRange(promotion),

								campaignType: data.campaign_type,
								name: data.name,
								code: data.campaign_code,
								value: promotion?.calculation_rule?.value_amount ?? 0,
								status: data?.status,
								pinRequired: data?.pin_required,
								generation: data?.pin_creation_method,
								description: data?.description,
								channels:
									promotion?.redemption_channels
										?.map((channel) => channel.redemption_channel_code)
										.join(', ') ?? '',
							},
							performance,
						});
					},
				);
			}
		},
		[requestorId],
	);

	const duplicateCampaign = (campaign) => {
		setIsDuplicateMode(true);
		setIsCampaignDataFetching(true);
		getCampaignDetails(campaign, true);
	};

	const getMaxRedemptionsValue = (campaignData, isVoucher) => {
		// For Voucher and Targeted Promotion - same logic
		if (isVoucher || isTargetedPromotion(campaignData.pinRequired)) {
			// If Unlimited selected - return INTEGER_MAX_VALUE
			if (!campaignData.redemptionsAllowed) {
				return INTEGER_MAX_VALUE;
			}
			// If Specified - return value from UI or default 1
			if (
				campaignData.maxNumberOfRedemptions &&
				+campaignData.maxNumberOfRedemptions > 0
			) {
				return +campaignData.maxNumberOfRedemptions;
			}
			return DEFAULT_REDEMPTIONS_TARGETED;
		}

		// For General Promotion
		// If Unlimited selected - return INTEGER_MAX_VALUE
		if (!campaignData.redemptionsAllowed) {
			return INTEGER_MAX_VALUE;
		}
		// If Specified - return value from UI
		if (
			campaignData.maxNumberOfRedemptions &&
			+campaignData.maxNumberOfRedemptions > 0
		) {
			return +campaignData.maxNumberOfRedemptions;
		}
		return INTEGER_MAX_VALUE;
	};

	const onSubmit = (type) => {
		const existingCampaign = filteredCampaigns.find(
			(campaign) => campaign.campaignName === formData.campaignName,
		);
		const isVoucher = isVoucherCampaign(editedFormData.campaignType);

		const isEdit = !!existingCampaign;

		// date and time needs to be fixed by the BE team At the moment fixed value but campaign can be created with no StartDateTime or no EndDateTime
		const issuanceStartTime =
			editedFormData.validityPeriod.startTime || '00:00';
		const issuanceEndTime = editedFormData.validityPeriod.endTime || '00:00';
		let issuanceStartDate = editedFormData.validityPeriod.startDate;
		let issuanceEndDate = editedFormData.validityPeriod.endDate;

		if (issuanceStartDate && !issuanceStartDate.includes('T')) {
			issuanceStartDate = `${issuanceStartDate}T${issuanceStartTime}:01`;
		}
		if (issuanceEndDate && !issuanceEndDate.includes('T')) {
			issuanceEndDate = `${issuanceEndDate}T${issuanceEndTime}:59`;
		}

		const calculationType = getCalculationType(isVoucher, editedFormData);

		const redemptionChannels = getRedemptionChannels(
			editedFormData.channels.selected,
		);

		const fareCodes = getFareCodes(editedFormData.fareCodes.selected, true);

		const settlementDetailsData = getSettlementDetailsData(
			editedFormData.settlementData,
			isVoucher,
		);
		const fareClasses = getFareClasses(editedFormData.fareTypes);

		const fulfilmentsRules = getFulfilmentsRules(editedFormData.fulfilments);

		const { fareQualifiers, allowedFareQualifier } = parseRailcardsData(
			editedFormData.railcards,
		);

		const formattedForm = {
			campaign_code: editedFormData.promotionCode,
			account_code: existingCampaign?.accountName ?? 'srtqa',
			name: editedFormData.campaignName,
			description: editedFormData.description,
			// country_code: editedFormData.countryCode ?? 'GB',
			status: editedFormData.active ? 'ACTIVE' : 'INACTIVE',
			pin_required: editedFormData.pinRequired,
			pin_creation_method: 'RANDOM10',
			campaign_type: isVoucher ? 'VOUCHER' : 'PROMOTION',
			issuance_rule: {
				issuance_start_date: isEdit ? editedFormData.startDate : '',
				issuance_end_date: isEdit ? editedFormData.endDate : '',
				max_number_of_issuances: editedFormData.quantityAllowed
					? +editedFormData.quantityAllowedValue
					: INTEGER_MAX_VALUE,
			},
			redemption_rule: getRedemptionRule(
				editedFormData.validityPeriod,
				getMaxRedemptionsValue(editedFormData, isVoucher),
			),

			redemption_channels: redemptionChannels,
			travel_restrictions: {
				travel_date_time: getTravelDateTime(editedFormData.dateOfTravel),
				routes: [],
				fare_classes: fareClasses,
				service_classes: [],
				fare_codes: fareCodes,
				marketing_carriers: [],
				daily_schedules: [],
				ticket_delivery_rules: fulfilmentsRules,
				travel_restriction_fare_qualifiers: fareQualifiers,
				travel_restriction_allowed_fare_qualifier: allowedFareQualifier,
			},
			calculation_rule: {
				rounding_method: CALCULATION_RULE_ROUNDING_METHOD,
				calculation_type: calculationType,
				value_amount: isVoucher
					? +editedFormData.discountValue
					: +editedFormData.adultDiscountValue,
				value_amount_child: isVoucher
					? null
					: +editedFormData.childDiscountValue,
				value_currency: 'GBP',
				min_price_threshold_amount: editedFormData.minimumSpentValue
					? +editedFormData.minimumSpentValue
					: 0,
				min_price_threshold_currency: 'GBP',
				settlement_details: settlementDetailsData,
			},

			...getTravelerRestrictions(editedFormData.validPassengers),
		};

		if (
			editedFormData.validRoutes?.isSpecified &&
			editedFormData.validRoutes.selected
		) {
			const routes = getRoutes(editedFormData.validRoutes.selected, true);

			formattedForm.travel_restrictions.routes = routes;
		}

		if (isVoucher) {
			delete formattedForm.calculation_rule.rounding_method;
		}

		switch (type) {
			case 'reset':
				setEditedFormData(formData);
				setFormData(formData);
				break;
			case 'delete':
				if (isVoucher) {
					deleteVoucherCampaign(
						{ campaignCode: existingCampaign.promotionCode },
						requestorId,
						(error) => {
							setAlertText(
								error.message ?? 'Failed to delete voucher campaign',
							);
						},
						() => {
							handleCampaigns();
							setIsCampaignModalOpen(false);
						},
					);
				} else {
					deletePromotionCampaign(
						{ campaignCode: existingCampaign.promotionCode },
						requestorId,
						(error) => {
							setAlertText(
								error.message ?? 'Failed to delete promotion campaign',
							);
						},
						() => {
							handleCampaigns();
							setIsCampaignModalOpen(false);
						},
					);
				}
				break;
			case 'saveActive':
			case 'saveInactive': {
				if (isEdit) {
					if (isVoucher) {
						updateVoucherCampaign(
							formattedForm,
							requestorId,
							(error) => {
								setAlertText(
									error.message ?? 'Failed to update voucher campaign',
								);
							},
							() => {
								handleCampaigns();
								setFormData(editedFormData);
								setEditedFormData(editedFormData);
								setIsCampaignModalOpen(false);
							},
						);
					} else {
						updatePromotionCampaign(
							formattedForm,
							requestorId,
							(error) => {
								setAlertText(
									error.message ?? 'Failed to update promotion campaign',
								);
							},
							() => {
								handleCampaigns();
								setFormData(editedFormData);
								setEditedFormData(editedFormData);
								setIsCampaignModalOpen(false);
							},
						);
					}
				} else if (isVoucher) {
					createVoucherCampaign(
						formattedForm,
						requestorId,
						(error) => {
							setAlertText(
								error.message ?? 'Failed to create voucher campaign',
							);
						},
						() => {
							handleCampaigns();
							setFormData(editedFormData);
							setEditedFormData(editedFormData);
							setIsCampaignModalOpen(false);
						},
					);
				} else {
					createPromotionCampaign(
						formattedForm,
						requestorId,
						(error) => {
							setAlertText(
								error.message ?? 'Failed to create promotion campaign',
							);
						},
						() => {
							handleCampaigns();
							setFormData(editedFormData);
							setEditedFormData(editedFormData);
							setIsCampaignModalOpen(false);
						},
					);
				}
				break;
			}
			case 'close':
				setIsCampaignModalOpen(false);
				setIsEditMode(false);
				setIsDuplicateMode(false);
				break;
			default:
				break;
		}
		setModalConfig({ isOpen: false, type: null });
	};

	const abortControllerRef = useRef(null);

	const handleStationsSearch = async (
		_stationRequestType,
		stationStartsWith,
	) => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		if (!stationStartsWith) {
			return;
		}

		abortControllerRef.current = new AbortController();

		setIsStationLoading(true);

		try {
			const response = await asyncApiCall(
				stationSearchApi,
				{
					searchText: stationStartsWith,
					otherStationCode: '',
					includeTravelCards: true,
				},
				abortControllerRef.current,
			);

			const responseResults =
				response.successResponse.data.StationSearchResults;

			const results = responseResults.map(
				({ StationSearchResultName, StationSearchResultCode }) => ({
					StationSearchResultName,
					StationSearchResultCode,
				}),
			);

			setStations(results);
		} catch (error) {
			if (error.errorResponse.code !== 20) {
				setAlertText(error.errorResponse.message);
			}
		} finally {
			abortControllerRef.current = null;

			setIsStationLoading(false);
		}
	};

	const clearStations = () => {
		setStations([]);
	};

	const issuanceOfferFlow = useIssuanceOfferFlow();

	const shouldShowSpinner =
		isCampaignDataFetching ||
		issuanceOfferFlow.isIssuanceOfferFetching ||
		issuanceOfferFlow.isAssignIssuanceOfferFetching;

	let spinnerLabel = null;

	if (issuanceOfferFlow.isAssignIssuanceOfferFetching) {
		spinnerLabel = 'Assigning Offer';
	} else if (issuanceOfferFlow.isIssuanceOfferFetching) {
		spinnerLabel = 'Generating Offer';
	}

	const handleMultipleOfferSubmit = (multipleOfferData, campaign) => {
		const campaignCode = campaign.promotionCode;
		const { csvFileData, assignToCustomerId, quantity } = multipleOfferData;
		setIsMultipleOfferFetching(true);
		setMultipleOfferSuccess(false);

		const multipleOfferErrorMessage =
			'There was an error generating this offer. Please check the form and the file used and try again';

		const ids = csvFileData
			.map((row) => row.id)
			.filter((id) => !!id && id.trim() !== '');

		const data = {
			campaign_code: campaignCode,
			description: campaign.description || '',
			value_currency: 'GBP', // hardcoded (we need to configure in future)
			value_amount: null,
			value_amount_child: null,
			redeemable_customer_profile_ids: assignToCustomerId ? ids : [],
			issued_per_customer: Number(quantity),
			issued_customer_profile_id: loginId,
		};

		const issueMultipleOfferApi = isVoucherCampaign(campaign.campaignType)
			? issueMultipleVoucher
			: issueMultiplePromotion;

		issueMultipleOfferApi(
			data,
			requestorId,
			() => {
				setMultipleOfferError(multipleOfferErrorMessage);
				setIsMultipleOfferFetching(false);
			},
			(response) => {
				const enrichedCsvData = enrichCsvData(
					csvFileData,
					response,
					campaignCode,
					assignToCustomerId,
				);
				handleCsvDownload(enrichedCsvData, campaignCode);
				setIsMultipleOfferFetching(false);
				setMultipleOfferSuccess(true);
			},
		);
	};

	return (
		<div className="container">
			<div className="row">
				<div className="col-12">
					{hasSettings && (
						<>
							<GlobalCampaignStyle />
							<Header activeTab={4} />
							<CampaignsWrapper>
								<Campaigns
									reports={reports}
									createNewCampaign={() => createNewCampaign()}
									handleSearch={(e) => handleSearch(e)}
									campaigns={filteredCampaigns}
									onViewReports={onViewReports}
									onDuplicate={duplicateCampaign}
									onViewEdit={handleViewEdit}
									formData={editedFormData}
									setEditedFormData={setEditedFormData}
									getStations={handleStationsSearch}
									stations={stations}
									clearStations={clearStations}
									isStationLoading={isStationLoading}
									modalConfig={modalConfig}
									isCampaignModalOpen={isCampaignModalOpen}
									setModalConfig={setModalConfig}
									onSubmit={onSubmit}
									isEditMode={isEditMode}
									isDuplicateMode={isDuplicateMode}
									issuedCampaignCode={issuanceOfferFlow.issuedCampaignCode}
									onIssuanceOfferSubmit={
										issuanceOfferFlow.handleIssuanceOfferSubmit
									}
									onAssignIssuanceOfferSubmit={
										issuanceOfferFlow.handleAssignIssuanceOfferSubmit
									}
									issuanceOffer={issuanceOfferFlow.issuanceOffer}
									issuanceOfferError={issuanceOfferFlow.issuanceOfferError}
									isAssignIssuanceOfferFetching={
										issuanceOfferFlow.isAssignIssuanceOfferFetching
									}
									isIssuanceOfferAssigned={
										issuanceOfferFlow.isIssuanceOfferAssigned
									}
									assignIssuanceOfferError={
										issuanceOfferFlow.assignIssuanceOfferError
									}
									onGenerateOfferCancel={
										issuanceOfferFlow.handleGenerateOfferCancel
									}
									onMultipleOfferSubmit={handleMultipleOfferSubmit}
									isMultipleOfferFetching={isMultipleOfferFetching}
									multipleOfferError={multipleOfferError}
									multipleOfferSuccess={multipleOfferSuccess}
									realmConfig={{
										realmOptions: filteredAccountRealms,
										activeRealm,
										onRealmChange,
									}}
								/>
							</CampaignsWrapper>
						</>
					)}
				</div>
			</div>
			<ExtendedSnackbar
				id="srtCampaignSnackBar"
				open={alertText !== ''}
				message={alertText}
				onClose={() => setAlertText('')}
				autoHideDuration={4000}
			/>

			<SpinnerDialog open={shouldShowSpinner} label={spinnerLabel} />
		</div>
	);
};

CampaignsDashboard.propTypes = {
	loginId: PropTypes.string,
	settings: PropTypes.object,
	intl: PropTypes.object,
	railCards: PropTypes.object,
	activeRealm: PropTypes.object,
	setActiveRealm: PropTypes.func,
};

const mapStateToProps = (state) => ({
	loginId: state.getIn(['settings', 'UserLoginId']),
	settings: state.get('settings'),
	railCards:
		state.getIn(['settings', 'Railcards'])?.get('RailCardTypes')?.toJS?.() ||
		[],
	activeRealm: state.getIn(['auth', 'activeRealm']),
});

const mapDispatchToProps = {
	setActiveRealm,
};

export default injectIntl(
	connect(mapStateToProps, mapDispatchToProps)(CampaignsDashboard),
);
